import React from "react";
import { withRefProp } from "../../../utils/withRefProp";
import Button from "../../common/Button/Button";
import TextInput from "../TextInput/TextInput";
import type { TextInputProps } from "../TextInput/TextInput";

export interface TimeInputProps extends Omit<TextInputProps, "value" | "onValueChange" | "onChange" | "type"> {
  value?: string | null;
  onValueChange?: (value: string) => void;
  showSeconds?: boolean;
  allowNow?: boolean;
}

const TimeInput = withRefProp(
  ({
    value,
    onValueChange,
    showSeconds = false,
    allowNow = false,
    disabled,
    error,
    fullWidth = false,
    ref,
    onBlur,
    placeholder,
    endAdornment,
    ...otherProps
  }: TimeInputProps) => {
    const inputValue = value ?? "";
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const [inputType, setInputType] = React.useState<React.HTMLInputTypeAttribute>(
      allowNow && inputValue === "now" ? "text" : "time",
    );
    const setInputRef = React.useCallback(
      (nextInput: HTMLInputElement | null) => {
        inputRef.current = nextInput;
        if (typeof ref === "function") ref(nextInput);
        else if (ref) ref.current = nextInput;
      },
      [ref],
    );
    const syncInputTypeForValue = React.useCallback(
      (nextValue: string) => {
        setInputType(allowNow && nextValue.trim().toLowerCase() === "now" ? "text" : "time");
      },
      [allowNow],
    );
    const handleValueChange = React.useCallback(
      (nextValue: string) => {
        const normalizedInputValue = nextValue.trim().toLowerCase();
        const normalizedValue =
          allowNow && inputValue === "now" && "now".startsWith(normalizedInputValue)
            ? ""
            : normalizedInputValue === "now"
              ? "now"
              : nextValue;
        syncInputTypeForValue(normalizedValue);
        onValueChange?.(normalizedValue);
      },
      [allowNow, inputValue, onValueChange, syncInputTypeForValue],
    );
    const handleNowClick = React.useCallback(() => {
      const nextValue = inputValue === "now" ? "" : "now";
      syncInputTypeForValue(nextValue);
      if (inputRef.current) {
        inputRef.current.type = nextValue === "now" ? "text" : "time";
        inputRef.current.value = nextValue;
      }
      onValueChange?.(nextValue);
    }, [inputValue, onValueChange, syncInputTypeForValue]);

    React.useEffect(() => {
      syncInputTypeForValue(inputValue);
    }, [inputValue, syncInputTypeForValue]);

    return (
      <TextInput
        {...otherProps}
        ref={setInputRef}
        type={inputType}
        step={inputType === "time" ? (showSeconds ? 1 : 60) : undefined}
        value={inputValue}
        onValueChange={handleValueChange}
        disabled={disabled}
        error={error}
        fullWidth={fullWidth}
        placeholder={placeholder ?? (showSeconds ? "HH:MM:SS or now" : "HH:MM or now")}
        onBlur={onBlur}
        endAdornment={
          allowNow ? (
            <>
              {endAdornment}
              <Button
                type="button"
                size="sm"
                variant="outline"
                intent="secondary"
                label="Now"
                selected={inputValue === "now"}
                aria-pressed={inputValue === "now"}
                onClick={handleNowClick}
                disabled={disabled}
              />
            </>
          ) : (
            endAdornment
          )
        }
      />
    );
  },
);

export default TimeInput;
